import helpers from "@/helpers.js";
import moment from "moment";
import CategoriesServices from "@/services/Category";
import ProductsServices, { isValidProductGuid } from "@/services/Product";
import Api from "@/services/Api";
import { getProductFormLookups } from "@/services/productFormLookups";

export default async function editProduct(pr) {
  let [detailsData, existing_images, exclusive_entities, lookups] = await Promise.all([
    Api(true)
      .post("product/details", { guid: pr.guid })
      .then((r) => r.data)
      .catch(() => null),
    Api(true)
      .post("product/images", { guid: pr.guid })
      .then((r) => r.data)
      .catch(() => []),
    ProductsServices.get_exclusive_entity(pr, null),
    getProductFormLookups(),
  ]);

  // If brand/supplier was created after the lookups cache was filled, refresh once.
  const supplierRef = (detailsData && detailsData.supplier != null ? detailsData.supplier : pr.supplier);
  const brandRef = (detailsData && detailsData.brand != null ? detailsData.brand : pr.brand);
  const supplierMissing =
    supplierRef &&
    !lookups.supplier_data.some((s) => s.id == supplierRef || s.guid == supplierRef);
  const brandMissing =
    brandRef &&
    !lookups.brand_data.some((b) => b.id == brandRef || b.guid == brandRef);
  if (supplierMissing || brandMissing) {
    lookups = await getProductFormLookups(true);
  }

  if (detailsData && typeof detailsData === "object" && !Array.isArray(detailsData)) {
    pr = { ...pr, ...detailsData };
  }

  const isActive = pr.status === 1 || pr.status === "1" || pr.status === true;

  const {
    product_categories,
    product_accessories,
    details_types,
    accessories,
    unit,
    brand_data,
    brand,
    warranties,
    vat,
    managers,
    supplier_data,
    suppliers,
    pos_list,
    energy_efficiency,
    country_list,
  } = lookups;
  var selected_category = {};
  var selected_category_product = {};
  var selected_category_accessory = {};

  if (pr.is_accessory == 0) selected_category[pr.type_id] = true;
  else selected_category[pr.accessory_id] = true;

  if (pr.type_id) selected_category_product[pr.type_id] = true;
  if (pr.accessory_id) selected_category_accessory[pr.accessory_id] = true;

  const [category_attributes_list, category_accessories_attributes_list, category_services_list] = await Promise.all([
    Object.keys(selected_category_product).length
      ? CategoriesServices.getCategoryAttributes(selected_category_product, pr)
      : Promise.resolve([]),
    Object.keys(selected_category_accessory).length
      ? CategoriesServices.getCategoryAttributes(selected_category_accessory, pr)
      : Promise.resolve([]),
    CategoriesServices.getCategoryAttributes(selected_category, pr, null, "services"),
  ]);
  var category_attributes = helpers.markAllAttributesRequired(
    helpers.create_attributes_field(category_attributes_list, selected_category_product),
    isActive
  );
  var category_attributes_accessories = helpers.markAllAttributesRequired(
    helpers.create_attributes_field(category_accessories_attributes_list, selected_category_accessory),
    isActive
  );
  var category_services = helpers.create_attributes_field(category_services_list, selected_category);
  var existing_exclusive_attribute = exclusive_entities.attributes.filter((a) => a.category_id == "EXCLUSIVE");
  var exclusive_attributes = helpers.create_attributes_field(existing_exclusive_attribute, selected_category);
  var exclusive_options = exclusive_entities.attributes.sort((a, b) => (a.attribute_name < b.attribute_name ? -1 : 1)).filter((ee) => ee.attribute_value == null);
  var exclusive_attr_list = {};
  for (let i = 0; i < exclusive_options.length; i++) {
    if (!exclusive_attr_list[exclusive_options[i].attribute_id]) {
      exclusive_attr_list[exclusive_options[i].attribute_id] = {
        attributes: [],
        label: exclusive_options[i].attribute_name,
        id: exclusive_options[i].attribute_id,
      };
    }
    exclusive_attr_list[exclusive_options[i].attribute_id].attributes.push(exclusive_options[i]);
  }

  // Logic: Pre-select the existing brand
  var selected_brand_option = {};
  if (pr.brand) {
    // Find the label for the ID to show it correctly in tree-select
    // Note: pr.brand usually comes as ID from backend, but check if it's an object
    let foundBrand = brand_data.find((b) => b.id == pr.brand || b.guid == pr.brand);
    if (foundBrand) {
      selected_brand_option[foundBrand.id] = foundBrand.name || foundBrand.label;
    } else {
      // Fallback if ID exists but not found in list (rare)
      selected_brand_option[pr.brand] = pr.brand_name || "Unknown Brand";
    }
  }
  // -------------------

  // Logic: Pre-select the existing supplier and its code
  var selected_supplier_option = {};
  var initialSupplierCode = "";
  if (pr.supplier) {
    // Find the label for the ID to show it correctly in tree-select
    let foundSupplier = supplier_data.find((s) => s.id == pr.supplier || s.guid == pr.supplier);
    if (foundSupplier) {
      const opt = suppliers.find((s) => s.id == foundSupplier.id);
      selected_supplier_option[foundSupplier.id] = opt ? opt.label : (foundSupplier.name || foundSupplier.label);
      initialSupplierCode = foundSupplier.code ? String(foundSupplier.code).trim() : "";
    } else {
      // Fallback if ID exists but not found in list (rare)
      selected_supplier_option[pr.supplier] = pr.supplier_name || "Unknown Supplier";
    }
  }
  // ----------------------

  var defaultVat20 = vat.find((x) => Number(x.value) === 20 || String(x.label || x.value || "").includes("20"));
  var defaultVatId = defaultVat20 ? defaultVat20.id : pr.vat;
  var products_selected_types = {};
  var products_selected_accessory = {};

  // Resolve category selection from the same lists used for the tree so the key matches option.id (guid).
  // This handles (guid, related_entity) uniqueness: product category tree uses "products" list, accessory tree uses "accessories" list.
  var categoryGuidProduct = pr.type_id != null && pr.type_id !== "" ? String(pr.type_id) : null;
  var categoryGuidAccessory = pr.accessory_id != null && pr.accessory_id !== "" ? String(pr.accessory_id) : null;
  if (categoryGuidProduct) {
    var catProduct = product_categories.find((c) => String(c.id) === categoryGuidProduct || String(c.guid) === categoryGuidProduct);
    if (catProduct) {
      products_selected_types[catProduct.id] = pr.type_id_name || catProduct.name || catProduct.label;
    } else {
      products_selected_types[categoryGuidProduct] = pr.type_id_name || categoryGuidProduct;
    }
  }
  if (categoryGuidAccessory) {
    var catAccessory = product_accessories.find((c) => String(c.id) === categoryGuidAccessory || String(c.guid) === categoryGuidAccessory);
    if (catAccessory) {
      products_selected_accessory[catAccessory.id] = pr.type_id_name || catAccessory.name || catAccessory.label;
    } else {
      products_selected_accessory[categoryGuidAccessory] = pr.type_id_name || categoryGuidAccessory;
    }
  }

  var selected_services = [];
  if (exclusive_entities.selected_services.length > 0) {
    exclusive_entities.selected_services.map((es) => {
      selected_services[es.guid] = es.name;
    });
  }

  var selected_accessories_options = [];
  if (exclusive_entities.selected_accessories.length > 0) {
    exclusive_entities.selected_accessories.map((ea) => {
      selected_accessories_options[ea.guid] = ea.name;
    });
  }

  // When product has no manager, autofill from category (type_id or accessory_id)
  var managerValue = pr.manager;
  if ((managerValue == null || managerValue === "") && (pr.type_id || pr.accessory_id)) {
    try {
      const categoryId = pr.is_accessory ? pr.accessory_id : pr.type_id;
      const res = await Api(true).post("category/manager", { category_id: categoryId });
      if (res.data && res.data.manager != null) managerValue = res.data.manager;
    } catch (e) {
      console.error("Category manager autofill error:", e);
    }
  }

  // Get product_pos
  var product_pos_list = [];
  var selected_product_pos = {};
  const posRes = await Api(true).post("get/single/product/pos", { product_id: pr.guid }).catch(() => ({ data: [] }));
  product_pos_list = posRes.data || [];
  if (product_pos_list.length > 0) {
    product_pos_list.map((up) => {
      selected_product_pos[up.guid] = up.name;
    });
  }

  const formatAuditDate = (d) => helpers.formatAuditDateTime(d);
  const formatDateInput = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");
  const createdByDisplay = [pr.created_by_name, formatAuditDate(pr.created_at)].filter(Boolean).join(" — ") || "—";
  const updatedByDisplay = [pr.updated_by_name, formatAuditDate(pr.updated_at)].filter(Boolean).join(" — ") || "—";
  const erpStockDisplay =
    pr.erp_stock !== null && pr.erp_stock !== undefined && pr.erp_stock !== ""
      ? String(pr.erp_stock)
      : "—";

  const identifierRows = [
    [
      { value: pr.known_as, name: "known_as", label: "Emri I njohur universal", type: "text-field", style: "min-width: 32%", required: isActive, requiredWhenActive: true, required_text: "E detyrueshme" },
      { value: pr.barcode || "", name: "barcode", label: "Barkodi", type: "text-field", style: "min-width: 28%", required: !pr.no_barcode, required_text: "E detyrueshme", readonly: !!pr.no_barcode, disabled: !!pr.no_barcode, unique: { model: "product", field: "barcode", where: [{ column: "guid", condition: "!=", value: pr.guid }] } },
      {
        name: "generate_barcode_btn",
        label: "",
        type: "action-button",
        action: "generate_barcode",
        button_label: "Gjenero barkod",
        icon: "fa fa-barcode",
        style: "flex: 0 0 auto;",
        disabled: !!pr.no_barcode,
      },
      { name: "no_barcode", label: "Pa barkod", type: "checkbox-field", style: "flex: 0 0 auto; min-width: 80px; margin-top: 5px; white-space: nowrap;", value: pr.no_barcode ? 1 : 0 },
    ],
    [
      { value: pr.origin, name: "origin", type: "select-field", label: "Direct Exchange", style: "min-width: 55%", options: country_list, required: false },
      { name: "demo_version", label: "Versioni Demo", type: "checkbox-field", style: "flex: 0 0 auto; min-width: 120px; margin-top: 5px; white-space: nowrap;", value: pr.demo_version ? 1 : 0 },
    ],
  ];

  return {
    name: "Modifiko produktin",
    guid: pr.guid,
    export_label: true,
    submit: {
      label: "Modifiko",
      action: "submitProduct",
    },
    fields: [
      [
        { value: createdByDisplay, name: "created_by_display", label: "Krijuar nga", type: "text-field", style: "min-width: 49%", readonly: true, disabled: true },
        { value: updatedByDisplay, name: "updated_by_display", label: "Ndryshuar nga", type: "text-field", style: "min-width: 49%", readonly: true, disabled: true },
      ],
      [
        { value: erpStockDisplay, name: "erp_stock_display", label: "Stoku (Financa 5)", type: "text-field", style: "min-width: 49%", readonly: true, disabled: true },
        {
          name: "resync_financa_btn",
          label: "",
          type: "action-button",
          action: "resync_financa",
          button_label: "Sinkronizo F5",
          icon: "fa fa-refresh",
          style: "flex: 0 0 auto; margin-top: 5px;",
        },
      ],
      // 1. Type: product vs accessory | Status (top right)
      [
        { value: pr.is_recipe, name: "is_recipe", label: "I perbere", style: "min-width: 2%; display:none;", type: "checkbox-field" },
        { value: pr.is_accessory == 1 || pr.is_accessory === "1" || pr.is_accessory === true ? 1 : 0, name: "is_accessory", label: "Aksesor", style: "min-width: 2%; margin-top:5px;", type: "checkbox-field", readonly: false, disabled: false },
        { value: pr.stock_limit, name: "stock_limit", label: "Limiti i stokut", type: "number-field", style: "min-width: 15%; display: none !important;" },
        { value: pr.status != null ? pr.status : 0, name: "status", label: "Aktiv", type: "checkbox-field", style: "min-width: 24%; margin-top: 5px; margin-left: auto;" },
      ],
      // 2. Category (first so code/attributes make sense)
      [
        {
          name: "type_id",
          label: "Kategoria e produktit",
          type: "tree-select",
          style: "min-width: 100%",
          options: details_types,
          mode: "single",
          selected_options: products_selected_types,
          required: true,
          required_text: "E detyrueshme",
          vif: [{ field: "is_accessory", value_not: 1 }],
        },
      ],
      [
        {
          name: "accessory_id",
          label: "Kategoria e aksesoreve",
          type: "tree-select",
          style: "min-width: 100%",
          options: accessories,
          mode: "single",
          selected_options: products_selected_accessory,
          required: true,
          required_text: "E detyrueshme",
          vif: [{ field: "is_accessory", value_not: 0 }],
        },
      ],
      // 3. Identity: code, name, model
      [
        {
          value: pr.code,
          name: "code",
          label: "Kodi",
          type: "text-field",
          style: "min-width: 24%",
          required: true,
          left_icon: "fa-solid fa-hashtag",
          uppercase: true,
          // Automatic locking of code is temporarily disabled; to restore, re-add readonly/disabled.
          // readonly: true,
          // disabled: true,
        },
        { value: pr.name, name: "name", label: "Emri", type: "text-field", style: "min-width: 45%", required: true, required_text: "E detyrueshme" },
        {
          value: pr.model ?? "",
          name: "model",
          label: "Modeli",
          type: "text-field",
          style: "min-width: 29%",
          required: true,
          required_text: "E detyrueshme",
          unique: { model: "product", field: "model", where: [{ column: "guid", condition: "!=", value: pr.guid }] },
        },
      ],
      // 4. Images
      [
        {
          name: "product_images",
          label: "Fotot e Produktit (Detyrueshme)",
          type: "image-upload",
          style: "min-width: 100%",
          value: existing_images,
          required: false,
          required_text: "Ngarko të paktën një foto të produktit",
        },
      ],
      // 5. Description & warranty
      [
        { value: pr.unit, name: "unit", type: "select-field", label: "Njesia", style: "min-width: 24%; display: none !important;", options: unit, required: true },
        { value: pr.description, name: "description", label: "Pershkrimi", type: "text-field", style: "min-width: 45%", required: isActive, requiredWhenActive: true, required_text: "E detyrueshme" },
        { value: pr.warranty_id, name: "warranty_id", label: "Garancia", type: "select-field", options: warranties, style: "min-width: 29%", required: isActive, requiredWhenActive: true, required_text: "E detyrueshme" },
      ],
      // 6. Brand (selection left, checkbox right)
      [
        {
          value: pr.brand,
          name: "brand",
          label: "Marka",
          type: "tree-select",
          style: "min-width: 80%",
          options: brand,
          selected_options: selected_brand_option,
          mode: "single",
          send_label: true,
          required: true,
          required_text: "E detyrueshme",
          vif: [{ field: "is_new_brand", value_not: 1 }],
        },
        { name: "is_new_brand", label: "Shto Marke te re?", type: "checkbox-field", style: "min-width: 15%; margin-top: 5px;", value: 0 },
        { name: "new_brand_name", label: "Shkruaj emrin e Markes se re", type: "text-field", style: "min-width: 80%", required: true, vif: [{ field: "is_new_brand", value_not: 0 }] },
      ],
      // 7. Supplier (code auto-fills name; checkbox to create new with code + name)
      [
        {
          value: initialSupplierCode,
          name: "supplier_code",
          label: "Kodi i furnitorit",
          type: "text-field",
          style: "min-width: 24%",
          uppercase: true,
          left_icon: "fa-solid fa-hashtag",
          required: isActive,
          requiredWhenActive: true,
          required_text: "E detyrueshme",
          vif: [{ field: "is_new_supplier", value_not: 1 }],
        },
        {
          value: pr.supplier,
          name: "supplier",
          label: "Furnitori",
          type: "tree-select",
          style: "min-width: 56%",
          options: suppliers,
          selected_options: selected_supplier_option,
          mode: "single",
          send_label: true,
          required: isActive,
          requiredWhenActive: true,
          required_text: "E detyrueshme",
          vif: [{ field: "is_new_supplier", value_not: 1 }],
        },
        { name: "is_new_supplier", label: "Shto Furnitor te ri?", type: "checkbox-field", style: "min-width: 15%; margin-top: 5px;", value: 0 },
        { name: "new_supplier_code", label: "Kodi i Furnitorit te ri", type: "text-field", style: "min-width: 35%", required: true, vif: [{ field: "is_new_supplier", value_not: 0 }] },
        { name: "new_supplier_name", label: "Emri i Furnitorit te ri", type: "text-field", style: "min-width: 45%", required: true, vif: [{ field: "is_new_supplier", value_not: 0 }] },
      ],
      // 8. Pricing
      [{ name: "old_code", label: "Kodi i vjetër", type: "text-field", style: "min-width: 48%; display: none !important;", value: pr.old_code, required: false }],
      [
        { value: pr.default_price, name: "default_price", type: "number-field", label: "Cmimi baze", style: "min-width: 32%", required: isActive, requiredWhenActive: true, required_text: "E detyrueshme", format: "accounting" },
        {
          name: "has_offer_price",
          label: "Ka cmim ofertë",
          type: "checkbox-field",
          style: "min-width: 15%; margin-top: 5px;",
          value: pr.price != null && pr.default_price != null && Number(pr.price) !== Number(pr.default_price) ? 1 : 0,
        },
        {
          value: pr.price !== pr.default_price ? pr.price : pr.default_price,
          name: "price",
          label: "Cmimi ofertë",
          type: "number-field",
          style: "min-width: 32%",
          required: isActive,
          requiredWhenActive: true,
          readonly: pr.price === pr.default_price,
          disabled: pr.price === pr.default_price,
          format: "accounting",
        },
        { value: defaultVatId, name: "vat", type: "select-field", label: "TVSH(%)", style: "min-width: 32%; display: none !important;", options: vat },
      ],
      // 9. Identifiers & origin
      ...identifierRows,
      // 9b. Product segment & expiration date
      [
        {
          value: pr.product_segment || null,
          name: "product_segment",
          label: "Segmenti i produktit",
          type: "select-field",
          style: "min-width: 48%",
          options: [
            { id: "basic", label: "Produkt bazik" },
            { id: "medium", label: "Medium" },
            { id: "premium", label: "Premium" },
          ],
          required: false,
        },
        {
          value: formatDateInput(pr.expiration_date),
          name: "expiration_date",
          label: "Date skadence",
          type: "date-field",
          style: "min-width: 48%",
          required: false,
        },
      ],
      // 10. Management (autofill from category when product has no manager; depends_on when category changes)
      [
        {
          value: managerValue,
          name: "manager",
          label: "Menaxher",
          type: "select-field",
          style: "min-width: 45%",
          options: managers,
          required: false,
          depends_on: "type_id",
          api_call: "category/manager",
          vif: [{ field: "is_accessory", value_not: 1 }],
        },
        {
          value: managerValue,
          name: "manager",
          label: "Menaxher",
          type: "select-field",
          style: "min-width: 45%",
          options: managers,
          required: false,
          depends_on: "accessory_id",
          api_call: "category/manager",
          vif: [{ field: "is_accessory", value_not: 0 }],
        },
        { value: pr.energy_efficiency, name: "energy_efficiency", label: "Klasi i energjise", type: "select-field", style: "min-width: 29%; display: none !important;", options: energy_efficiency, required: false },
      ],
      // 11. Packaging & sales (Paketimi hidden)
      [
        {
          value: pr.packaging,
          name: "packaging",
          label: "Paketimi",
          type: "select-field",
          style: "min-width: 24%; display: none !important;",
          options: [
            { id: 0, label: "Paketim i thjeshte" },
            { id: 1, label: "Kuti me 10 cope" },
            { id: 2, label: "Kuti me 20 cope" },
          ],
          required: false,
        },
        { value: pr.in_web, name: "in_web", label: "Shitet ne web", style: "min-width: 2%; margin-top:5px;", type: "checkbox-field" },
        { value: pr.in_store == null || pr.in_store === "" ? 1 : Number(pr.in_store), name: "in_store", label: "Shitet ne dyqan", style: "min-width: 2%; margin-top:5px;", type: "checkbox-field" },
      ],
      // 11b. Web categories (read-only, derived from product category path – last 3 levels)
      [{ name: "web_categories_display", label: "Kategoritë web (nga kategoria online e lidhur)", type: "web-categories-readonly", style: "min-width: 100%", vif: [{ field: "in_web", value: 1 }] }],
      // 12. Airbag warranty
      [
        {
          value: pr.warranty_airbag != null && pr.warranty_airbag !== "" ? Number(pr.warranty_airbag) : 0,
          name: "warranty_airbag",
          label: "Garanci Airbag",
          style: "min-width: 24%",
          type: "select-field",
          options: [
            { id: 0, label: "Nuk ka airbag" },
            { id: 3, label: "Airbag+3" },
          ],
          no_placeholder: true,
        },
        { value: pr.warranty_airbag_value, name: "warranty_airbag_value", type: "number-field", label: "Cmimi i airbag", style: "min-width: 45%", vif: [{ field: "warranty_airbag", value_not: 0 }] },
      ],
      // 13. Points of sale (hidden)
      [
        {
          name: "products_pos",
          label: "Poset ku mund te tregtohet ky produkt",
          type: "tree-select",
          style: "width: 100%; display: none !important;",
          options: pos_list,
          selected_options: selected_product_pos,
          mode: "multiple",
          required: false,
        },
      ],
      // 14. Category attributes & services (collapsible)
      [
        { data: [], name: "attributes", label: "Atribute", type: "attributes", options: category_attributes, style: "min-width: 100%", depends_on: "type_id", api_call: "category/attributes", vif: [{ field: "is_accessory", value_not: 1 }], collapsible: true, require_all: isActive, required: isActive, requiredWhenActive: true, required_text: "Të gjitha atributet janë të detyrueshme", exclude_product_guid: isValidProductGuid(pr.guid) ? pr.guid.trim() : null },
        { data: [], name: "attributes_accessories", label: "Atribute", type: "attributes", options: category_attributes_accessories, style: "min-width: 100%", depends_on: "accessory_id", api_call: "category/attributes", vif: [{ field: "is_accessory", value_not: 0 }], collapsible: true, require_all: isActive, required: isActive, requiredWhenActive: true, required_text: "Të gjitha atributet janë të detyrueshme", exclude_product_guid: isValidProductGuid(pr.guid) ? pr.guid.trim() : null },
      ],
      [{ data: [], name: "services", label: "Sherbime", type: "attributes", options: category_services, style: "min-width: 100%", depends_on: "type_id", api_call: "category/services", collapsible: true }],
      // 15. Exclusive relations
      [
        {
          name: "exclusive_accessories",
          label: "Aksesore ekskluzive",
          type: "tree-select",
          style: "width: 100%",
          options: exclusive_entities.accessories.map((e) => {
            e.label = e.name;
            e.id = e.guid;
            return e;
          }),
          selected_options: selected_accessories_options,
          mode: "multiple",
          required: false,
          vif: [{ field: "is_accessory", value_not: 1 }],
        },
      ],
      [
        {
          name: "exclusive_services",
          label: "Sherbime ekskluzive",
          type: "tree-select",
          style: "width: 100%",
          options: exclusive_entities.services_with_services_types,
          selected_options: selected_services,
          mode: "multiple",
          required: false,
        },
      ],
      [
        {
          name: "exclusive_attributes",
          label: "Atribute ekskluzive",
          type: "attributes",
          is_exclusive: true,
          exclusive_options: exclusive_attr_list,
          style: "width: 100%",
          options: exclusive_attributes,
          selected_options: [],
          required: false,
          collapsible: true,
        },
      ],
      // Composite bundle
      [
        {
          value: pr.is_composite ? 1 : 0,
          name: "is_composite",
          label: "Produkt kompozit (paketë)",
          type: "checkbox-field",
          style: "min-width: 100%; margin-top: 8px;",
        },
      ],
      [
        {
          name: "composite_items",
          label: "Komponentët e paketës",
          type: "composite-items",
          style: "min-width: 100%",
          data: (pr.composite_items || []).map((item, idx) => ({
            _key: "existing_" + idx,
            component_product_guid: item.component_product_guid || item.component_code || null,
            component_code: item.component_code || null,
            quantity: item.quantity,
            label: item.label || (item.component_code ? item.component_code + " — " + (item.component_name || "") : ""),
          })),
          exclude_product_guid: isValidProductGuid(pr.guid) ? pr.guid.trim() : null,
          required: false,
          vif: [{ field: "is_composite", value_not: 0 }],
        },
      ],
      // Delisto (end of card)
      [
        { value: pr.delisted ? 1 : 0, name: "delisted", label: "Delisto", type: "checkbox-field", style: "min-width: 20%; margin-top: 8px;" },
        { value: pr.delisted_reason || "", name: "delisted_reason", label: "Koment (pse po delistohet)", type: "text-field", style: "min-width: 70%", required: true, vif: [{ field: "delisted", value: 1 }] },
      ],
    ],
  };
}
